import { omit, uniq, isEmpty } from 'lodash'
import moment from 'moment/moment'
import mustache from 'mustache'
import { call, select } from 'redux-saga/effects'

import VALIDATION_MESSAGES from 'core/data-model/constants/validation-messages'
import { calculateAge, convertEntryAge } from 'core/service/insured/birthdate'
import { formatAge } from 'core/service/lib/age-format'
import { quickQuotePolicy } from 'core/service-wrapper/partner-api/handler-request/service-proposal'
import { partnerApiUtils } from 'core/service-wrapper/partner-api/handler-request/service-utils'
import { getSelectedDisplayProductCode } from 'quick-quote/product-selection/selectors'
import * as quickQuoteSelector from 'quick-quote/selectors'
import { getPartnerAvailableRiders } from 'quick-quote/selectors/partner-selector'

function mappingToViewConfiguration(productCategories, agentType) {
  const config = {
    productList: [],
    productCategoryList: [],
    productsInCategory: {},
  }
  let distinctProducts = new Set()

  for (const category of productCategories) {
    let hasSellableProduct = false
    for (const item of category.packages) {
      if (item.rightToSell === 'Y') {
        hasSellableProduct = true
      }

      if (item.agentUserTypes.length === 0 || item.agentUserTypes.includes(agentType)) {
        if (!(category.productCategory in config.productsInCategory)) {
          config.productsInCategory[category.productCategory] = []
        }
        distinctProducts.add(item.packageCode)
        if (item.additionalFields.service_wrapper === 'legacy') {
          config.productsInCategory[category.productCategory].push(item.defaultPlanCode)
        } else {
          config.productsInCategory[category.productCategory].push(item.packageCode)
        }
      }
    }

    if (hasSellableProduct) {
      config.productCategoryList.push({
        id: category.productCategory,
        title: category.title,
        slug: category.title.toLowerCase(),
        description: category.description,
        shortDescription: category.description,
      })
    }
  }

  config.productList = [...distinctProducts]

  return config
}

/**
 * mappingPeriod
 * @param period {string}
 */
function mappingPeriod(period) {
  if (period.endsWith('A')) {
    return {
      type: 'limited_to_age',
      value: parseInt(period),
    }
  } else if (period.endsWith('D') || period.endsWith('P')) {
    return {
      type: 'limited_by_duration',
      value: parseInt(period),
    }
  }
}

const convertAgeSymbol = (symbol) => {
  if (symbol === '0m') return '0y'
  return symbol
}

const entryAgeToMonth = (entryAge) => {
  if (entryAge.unit === 'year') {
    return entryAge.value * 12
  } else if (entryAge.unit === 'month') {
    return entryAge.value
  } else {
    return 0
  }
}

const betweenEntryAge = (birthdate, minimum, maximum) => {
  const insuredAgeMonth = entryAgeToMonth(calculateAge(birthdate, moment().format('DD/MM/YYYY')))
  const minMonth = entryAgeToMonth(convertEntryAge(minimum))
  const maxMonth = entryAgeToMonth(convertEntryAge(maximum))

  return insuredAgeMonth >= minMonth && insuredAgeMonth <= maxMonth
}

/**
 * mappingToDisplayProducts
 * @param birthdate {string}
 * @param data {Record<string, any>}
 * @param showRiderUnsellable {boolean}
 */
function mappingToDisplayProducts(birthdate, data, showRiderUnsellable) {
  /** @type {Record<string, DisplayProduct[]>} */
  const displayProducts = {
    sellable: [],
    unsellable: [],
  }
  /** @type {Record<string, CacheRider>} */
  const ridersInProduct = {}
  /** @type {DisplayProduct} */
  let defaultSelectProduct = undefined

  for (const product of data.products) {
    let key = 'sellable'
    if (product.rightToSell !== 'Y' || product.rightToBuy !== 'Y') {
      key = 'unsellable'
    }

    const displayProduct = {
      code: product.productPlanCode,
      category: product.productCategory,
      name: product.productNameEN,
      productRefName: product.productPlanName,
      nameThai: product.productName,
      displayName: product.displayNameEN,
      displayNameLanguage: 'ENG',
      fullNameThai: product.displayName,
      basicPlanCode: product.productBasicPlanCode,
      _basicPlanCode: product.productPlanCode === product.productBasicPlanCode ? undefined : product.productPlanCode,
      basicPlanName: product.productBasicPlanName,
      basicPlanCodeOption: [],
      description: product.productShortDescription,
      benefits: [],
      paymentPeriod: mappingPeriod(product.paymentPeriod),
      coveragePeriod: mappingPeriod(product.coveragePeriod),
      modelFactors: product.modalFactors.map(partnerApiUtils.modalFactor.toModalFactorId),
      riders: product.riderCodes,
      selectedRiders: product.selectedRiderCodes,
      requiredRiders: product.requiredRiderCodes,
      optionalRiders: [],
      licenses: product.additionalFields.licenses || [],
      entryAge: {
        // minimum: convertEntryAge(convertAgeSymbol(product.minAgeSymbol)),
        // maximum: convertEntryAge(convertAgeSymbol(product.maxAgeSymbol)),
        minimum: convertEntryAge(convertAgeSymbol(data.productInsuredMinAgeSymbol)),
        maximum: convertEntryAge(convertAgeSymbol(data.productInsuredMaxAgeSymbol)),
      },
      product: {
        insuredEntryAgeMinimum: partnerApiHandlerHelper.convertAgeSymbol(data.productInsuredMinAgeSymbol),
        insuredEntryAgeMaximum: partnerApiHandlerHelper.convertAgeSymbol(data.productInsuredMaxAgeSymbol),
      },
      insuredEntryAgeMinimum: product.minAgeSymbol,
      insuredEntryAgeMaximum: product.maxAgeSymbol,
      entrySumAssured: {
        minimum: product.minSA,
        maximum: product.maxSA,
      },
      defaultSumAssured: product.defaultSA,
      sumAssuredMinimum: product.minSA,
      sumAssuredMaximum: product.maxSA,
      payerSelection: product.payerSelection,
      allowUserType: product.agentUserTypes,
      aura: {
        AURA_PRODUCT_CODE: product.auraProductCode,
        INPUT_XML_QUESTION_FILTERS: product.auraInputXMLQuestionFilter,
      },
      ...omit(product.overrideFields, ['product_groups']),
      useLegacy: product.additionalFields.service_wrapper === 'legacy',
      defaultSelected: product.defaultSelected,
    }

    if (!isEmpty(product.overrideFields?.product_groups)) {
      const productGroups = Object.values(product.overrideFields.product_groups).find(
        (group) => Array.isArray(group) && group.includes(displayProduct.code)
      )

      if (productGroups?.length) {
        displayProduct.productGroups = productGroups
      }
    }

    displayProducts[key].push(displayProduct)

    if (key === 'unsellable') {
      continue
    }

    if (product.defaultSelected) {
      defaultSelectProduct = displayProduct
    }

    // mapping rider
    const overrideMessageErrorOfRider = (rider, ridersSameCode) => {
      if ('InsuredAgeRule' in rider.errorMap) {
        const minAgeValue = ridersSameCode.map((r) => r.minAge).sort((a, b) => a.age - b.age)[0]
        const maxAgeValue = ridersSameCode
          .map((item) => item.maxAge)
          .sort((a, b) => a.age - b.age)
          .reverse()[0]

        const minAge = minAgeValue === 0 ? { value: 1, unit: 'month' } : { value: minAgeValue, unit: 'year' }
        const maxAge = { value: maxAgeValue, unit: 'year' }

        rider.errorMap['InsuredAgeRule'] = {
          ...rider.errorMap['InsuredAgeRule'],
          message: mustache.render(VALIDATION_MESSAGES.RULE_RIDER_INSURED_AGE, {
            minimumAge: formatAge(minAge),
            maximumAge: formatAge(maxAge),
          }),
        }
      }

      return rider
    }
    const riders = {}
    for (let rider of product.riders.filter((r) => r.visible)) {
      riders[rider.riderCode] = rider
    }
    if (showRiderUnsellable) {
      for (let rider of product.riders.filter((r) => r.rightToSell === 'Y' && r.rightToBuy === 'N')) {
        const ridersSameCode = product.riders.filter((r) => r.riderGroup === rider.riderGroup)
        const haveBuyable = ridersSameCode.filter((r) => r.rightToSell === 'Y' && r.rightToBuy === 'Y').length > 0
        if (haveBuyable) {
          continue
        }

        if (ridersSameCode.length > 1) {
          riders[rider.riderCode] = overrideMessageErrorOfRider(rider, ridersSameCode)
        } else if (!riders[rider.riderCode]) {
          riders[rider.riderCode] = rider
        }
      }
    }
    ridersInProduct[displayProduct.code] = { ...ridersInProduct[displayProduct.code], ...riders }
  }

  return {
    displayProducts: displayProducts.sellable,
    unsellableProducts: displayProducts.unsellable,
    defaultSelectProduct:
      defaultSelectProduct ?? displayProducts.sellable.length
        ? defaultProduct(birthdate, displayProducts.sellable)
        : defaultProduct(birthdate, displayProducts.unsellable),
    ridersInProduct,
  }
}

function defaultProduct(birthdate, products) {
  if (products.length === 1) {
    return products[0]
  }

  const defaultSelected = products.find((product) => product.defaultSelected)
  if (defaultSelected) {
    return defaultSelected
  }

  return products.find((product) =>
    betweenEntryAge(birthdate, product.insuredEntryAgeMinimum, product.insuredEntryAgeMaximum)
  )
}

/**
 * createCalculatePayload
 * @param {{
 *   [excludeBasicPlan]: boolean,
 *   [excludeRider]: boolean,
 *   [excludeFund]: boolean,
 *   [calculateByPremium]: boolean,
 *   [calculateByPension]: boolean,
 * }} props
 * @returns {Generator<*, CalculatePayload, *>}
 */
function* createCalculatePayload(props = {}) {
  const {
    excludeBasicPlan = false,
    excludeRider = false,
    excludeFund = false,
    calculateByPremium = false,
    calculateByPension = false,
  } = props

  const state = yield select(quickQuoteSelector.getAppState)

  const selectedDisplayProductCode = getSelectedDisplayProductCode(state)
  const insured = quickQuotePolicy.insured(state)
  const payer = quickQuotePolicy.payer(state)
  const seller = yield call(quickQuotePolicy.seller, state)
  const availableRiders = getPartnerAvailableRiders(state)

  return {
    insured: {
      birthdate: insured.birthDate,
      gender: insured.sex,
      natureOfDutyCode: insured.mainNatureOfDuty,
    },
    payer: payer
      ? {
          birthdate: payer.birthDate,
          gender: payer.sex,
          natureOfDutyCode: payer.mainNatureOfDuty,
          relation: payer.relationship,
        }
      : null,
    seller: {
      distributorGroup: seller.distributorGroup,
      agentCode: seller.lbCode,
      userAgentType: seller.userAgentType,
    },
    basicPlan: excludeBasicPlan
      ? {}
      : omit(
          quickQuotePolicy.basicPlan(state),
          calculateByPremium
            ? ['basicSA', 'pensionAmount']
            : calculateByPension
            ? ['basicSA', 'pensionAmount']
            : ['pensionAmount', 'basicPremium']
        ),
    riders: excludeRider
      ? null
      : quickQuotePolicy.riders(state).map((r) => ({ riderCode: r.productRiderCode, riderSA: r.coverageValue })),
    availableRiderGroups:
      availableRiders && availableRiders[selectedDisplayProductCode]
        ? uniq(
            Object.values(availableRiders[selectedDisplayProductCode])
              .filter((it) => it.rightToSell === 'Y')
              .map((it) => it.riderGroup)
          )
        : [],
    ...(excludeFund ? {} : quickQuotePolicy.funds(state)),
  }
}

async function createCalculatePayloadWithExpect(state, { basicPlan, riders, isCustomLstuFunds, funds, lstuFunds }) {
  const insured = quickQuotePolicy.insured(state)
  const payer = quickQuotePolicy.payer(state)
  const seller = await quickQuotePolicy.seller(state)

  return {
    insured: {
      birthdate: insured.birthDate,
      gender: insured.sex,
      natureOfDutyCode: insured.mainNatureOfDuty,
    },
    payer: payer
      ? {
          birthdate: payer.birthDate,
          gender: payer.sex,
          natureOfDutyCode: payer.mainNatureOfDuty,
          relation: payer.relationship,
        }
      : null,
    seller: {
      distributorGroup: seller.distributorGroup,
      agentCode: seller.lbCode,
    },
    basicPlan: basicPlan,
    riders: riders,
    isCustomLstuFunds: isCustomLstuFunds,
    funds: funds,
    lstuFunds: lstuFunds,
    setting: {
      skipValidateFund: true,
      showInvestmentBenefitTableAsDisplay: true,
      skipGeneratePolicy: true,
    },
  }
}

export const partnerApiHandlerHelper = {
  mappingToViewConfiguration,
  mappingToDisplayProducts,
  createCalculatePayload,
  createCalculatePayloadWithExpect,
  mappingPeriod,
  convertAgeSymbol,
  betweenEntryAge,
}
